/**
 * Handing a place to the reader's own map app — TROOP-242.
 *
 * **This is a rendering change, not a data one.** The product geocodes nothing, stores no
 * coordinate, renders no map and learns nothing it did not already know: the address is already on
 * the screen for everybody who may read the event, and this hands that same text to the phone. A
 * map *inside* the product — geocoding, stored coordinates, a pin showing where a car full of youth
 * is right now — is §6's GPS-in-EXIF problem with a live feed attached and is still out.
 * `docs/domain-model.md` §13's out-of-scope bullet says which half moved and which did not.
 *
 * **It is the product's second link surface, and its mitigation is different.** A requirement's
 * `ExternalLink` is a URL a leader pasted, so the destination hostname renders visibly beside it
 * (`docs/architecture.md`). Nothing here is pasted: the origin is one of the two literals below,
 * the typed text is the query string and never the host, and it goes through
 * `encodeURIComponent`. **If a leader's text is ever concatenated into a URL rather than encoded
 * into its query, that whole argument is void and the link rule is simply broken** —
 * `MapLinkSurfaceTests` is what holds it, from both ends.
 *
 * **HTTPS universal links only.** Never `comgooglemaps://` or `maps://`: a custom scheme with no
 * app behind it does nothing at all, silently, which is most of CLAUDE.md's silent-failure list in
 * one line. Both forms below open the native app where it is installed and a web map where it is
 * not.
 *
 * **Detection lives here and nowhere else**, which is the whole reason this file exists rather than
 * a ternary at each call site. Two per-call-site checks is how one screen offers Apple Maps and
 * another does not, forever, with nothing failing.
 */

/**
 * Apple's universal link. Degrades to a web page on a machine with no Apple Maps.
 */
const APPLE_MAPS = 'https://maps.apple.com/?q=';

/**
 * Google's documented universal link (`api=1`). Opens the Google Maps app on Android and on iOS
 * where it is installed, and the web map where it is not.
 */
const GOOGLE_MAPS = 'https://www.google.com/maps/search/?api=1&query=';

/**
 * **Every Apple platform, not the iPhone alone.** Apple Maps exists on iPhone, iPad and Mac, so a
 * sniff narrowed to the phone would refuse two of the three devices it works on — and it side-steps
 * the known trap that iPadOS reports itself as `Macintosh`, where a pattern written to *exclude*
 * the Mac excludes the iPad by accident and nobody notices, because the Google link still works.
 */
const APPLE_PLATFORM = /iPhone|iPad|iPod|Macintosh|Mac OS X/;

/** One map app's offer of one place. */
export interface MapDestination {
  /** The app, named — this is what a reader chooses between when there is a choice. */
  app: 'Apple Maps' | 'Google Maps';
  href: string;
}

/** A place as the product holds it: a name, a street address, or one of the two. */
export interface Place {
  name?: string | null;
  address?: string | null;
}

/**
 * Where this reader can open this place, in the order to offer them.
 *
 * **Empty when there is nothing to search for**, which is the caller's cue to render no control at
 * all rather than a disabled one: there is no reason to state, and `Button`'s `disabledReason`
 * would demand one.
 *
 * **One entry everywhere but an Apple platform**, where the reader gets the choice. A one-item menu
 * is a menu nobody wants, so the single case is a plain link and the caller can tell the two apart
 * by the length of this array and by nothing else.
 *
 * `userAgent` is a parameter only so a test can ask for the other branch; no call site passes it,
 * and `MapLinkSurfaceTests` is what keeps that true.
 */
export function mapLinksFor(place: Place, userAgent: string = currentUserAgent()): MapDestination[] {
  // Name and address together when both exist: a geocoder resolves "Camp Carpenter, 300 Blondeau
  // Rd, Manchester NH" better than either half alone. Either one alone when that is all there is.
  const query = [place.name, place.address]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');

  if (!query) {
    return [];
  }

  // The one place the typed text meets a URL, and it is a query parameter both times.
  const google: MapDestination = { app: 'Google Maps', href: GOOGLE_MAPS + encodeURIComponent(query) };

  return APPLE_PLATFORM.test(userAgent)
    ? [{ app: 'Apple Maps', href: APPLE_MAPS + encodeURIComponent(query) }, google]
    : [google];
}

/**
 * The only read of the user agent in the client.
 *
 * Guarded because `navigator` is absent under a plain Node process — which is exactly where the
 * unit test runs, and an unguarded read there would throw rather than fall to the Google branch.
 */
function currentUserAgent(): string {
  return typeof navigator === 'undefined' ? '' : navigator.userAgent;
}
